import React, { useEffect, useState } from 'react';
import { BrowserRouter } from 'react-router-dom';
import ReactDOM from 'react-dom/client';
import { CssBaseline, ThemeProvider } from '@mui/material';
import { AuthenticationResult, EventType, PublicClientApplication } from '@azure/msal-browser';
import { MsalProvider } from '@azure/msal-react';

import { ConfigJsonProvider, useConfig } from './app/config/ConfigJsonProvider';
import { getMsalInstance, initializeMsalConfig } from './app/components/authentication/msalConfig';

import './index.scss';
import App from './app/App';
import reportWebVitals from './reportWebVitals';
import ProgressBar from './app/components/controls/ProgressBar';

import theme from './app/theme';
import ScrollToTop from './app/components/navigation/ScrollToTop';


const AppInitializer = () => {
  const { config, loading, error } = useConfig();
  const [msalInstance, setMsalInstance] = useState<PublicClientApplication | null>(null);

  useEffect(() => {
    const init = async () => {
      if (!config || loading || error) return;

      try {
        initializeMsalConfig(config);

        const instance = getMsalInstance();

        // Initialize 1x is veilig; MSAL v2 heeft idempotent gedrag, maar we doen het netjes
        await instance.initialize();

        // Redirect resultaten afhandelen (1 centrale plek)
        const redirectResult: AuthenticationResult | null = await instance.handleRedirectPromise();

        if (redirectResult?.account) {
          instance.setActiveAccount(redirectResult.account);
        }
        else {
          // Als er al accounts zijn (persisted), kies er eentje
          const accounts = instance.getAllAccounts();
          if (accounts.length > 0) {
            instance.setActiveAccount(accounts[0]);
          }
        }

        // Event callback: activeAccount bijhouden na login / token events
        instance.addEventCallback((event) => {
          if (event.eventType === EventType.LOGIN_SUCCESS && event.payload) {
            const payload = event.payload as AuthenticationResult;
            if (payload.account) {
              instance.setActiveAccount(payload.account);
            }
          }
        });

        setMsalInstance(instance);
      }
      catch (e) {
        console.error("Failed to initialize MSAL:", e);
      }
    };

    init();
  }, [config, loading, error]);


  if (loading || !msalInstance) {
    return <ProgressBar />;
  }

  return (
    <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
      <ScrollToTop />
      <MsalProvider instance={msalInstance}>
        <App />
      </MsalProvider>
    </BrowserRouter>
  );
};

const root = ReactDOM.createRoot(document.getElementById('root') as HTMLElement);

root.render(
  <React.StrictMode>
    <ConfigJsonProvider>
      <ThemeProvider theme={theme}>
        <CssBaseline /> { /* Provides global CSS reset and baseline styling */}
        <AppInitializer />
      </ThemeProvider>
    </ConfigJsonProvider>
  </React.StrictMode>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
